import {Feature} from "ol"
import {Point} from "ol/geom"
import {Style, Icon, Fill, Stroke, Text, Circle} from "ol/style"
import {Options} from "ol/style/Icon"
import VectorLayer from "ol/layer/Vector"
import {FeatureLike} from "ol/Feature"
import {TPoiProps} from "@ui/api/features"
import store from "@ui/stores"
import {colors} from "@ui/common/colors"
import mapService from "@ui/services/MapService"
import {FeatureLayers} from "./"

export const Icons: {[key: string]: string} = {}
export const SelectedIcons: {[key: string]: string} = {}
export const IconStyles: {[key: string]: ((feature: FeatureLike, resolution: number) => Style) | null} = {}

const alertErrorIcon = (signIconPath: string, feature: FeatureLike) => {
  const message = `Неизвестный знак! Название - ${signIconPath}, свойства - ${JSON.stringify(feature.getProperties())}`
  console.log(message)
  return "<svg></svg>"
}

export const getIconPath = (feature: FeatureLike, layerId: string) =>
  `${FeatureLayers[layerId].getProperties().iconPath}${feature.getProperties().icon_name}`

const getIcon = (iconPath: string, feature: FeatureLike): Promise<string> => {
  return new Promise((resolve, reject) => {
    fetch(`${iconPath}.svg`).then(async response => {
      if (response.status > 400) {
        alertErrorIcon(iconPath, feature)
        reject("icon not found")
      }
      resolve(response.text())
    })
    .catch(error => {
      alertErrorIcon(iconPath, feature)
      reject(error)
    })
  })
}

type TConstructStrokedSelectProps = {
  feature: FeatureLike
  getIconGeometry?: (feature: FeatureLike) => Point | undefined,
  layerId: string
  svgString: string
  selectedSvgString?: string
  iconPath: string
  styleKey?: string
  populateIcon: (svg: Document, feature: FeatureLike) => Document
}
const constructStyleWithStrokedSelect = ({
  feature,
  getIconGeometry,
  layerId,
  svgString,
  selectedSvgString,
  iconPath,
  styleKey,
  populateIcon = (svg, feature) => svg
}: TConstructStrokedSelectProps) => {
  Icons[iconPath] = svgString

  const svg = populateIcon(new DOMParser().parseFromString(svgString, "image/svg+xml"), feature)
  const url = URL.createObjectURL(new Blob([svg.documentElement.outerHTML], {type: 'image/svg+xml'}))

  if (!selectedSvgString) {
    svg.documentElement.childNodes.forEach((node) => {
      if (node.nodeType === 1) {
        const element = node as Element
        element.setAttribute("stroke-width", "1.5")
        element.setAttribute("stroke", "#0088FF")
      }
    })
    selectedSvgString = svg.documentElement.outerHTML
  }
  SelectedIcons[iconPath] = selectedSvgString
  const urlSelected = URL.createObjectURL(new Blob([selectedSvgString], {type: 'image/svg+xml'}))

  IconStyles[styleKey || iconPath] = (feature: FeatureLike, resolution: number) => new Style({
    ...(getIconGeometry && getIconGeometry(feature) ? {geometry: getIconGeometry(feature)} : {}),
    image: new Icon({
      src: feature.getProperties().selected ? urlSelected : url,
      crossOrigin: '*',
      ...(feature.getProperties().selected || !getIconGeometry || !getIconGeometry(feature) ? {anchor: [0.5, 1], anchorXUnits: "fraction", anchorYUnits: "fraction"} : {})
    })
  })
  mapService.getLayerById<VectorLayer<Feature>>(layerId)?.changed()
}

type TStrokedSelectProps = {
  layerId: string
  getIconGeometry?: (feature: FeatureLike) => Point | undefined
  getStyleKey?: (feature: FeatureLike) => string
  loadSelectedIcon?: boolean
  populateIcon?: (svg: Document, feature: FeatureLike) => Document
  getExtraStyles?: (feature: FeatureLike, resolution: number) => Style[]
}
export const styleWithStrokedSelect = ({
  layerId,
  getIconGeometry = () => undefined,
  getStyleKey,
  loadSelectedIcon,
  getExtraStyles = () => [],
  populateIcon = (svg, feature) => svg,
}: TStrokedSelectProps) => (
  feature: FeatureLike,
  resolution: number
) => {
  const iconPath = getIconPath(feature, layerId)
  const styleKey = getStyleKey ? getStyleKey(feature) : iconPath

  let styleFn = IconStyles[styleKey]
  if (!styleFn) {
    if (styleFn === null) {
      setTimeout(() => mapService.getLayerById<VectorLayer<FeatureLike>>(layerId)?.changed(), 1000)
    } else {
      IconStyles[styleKey] = null
      const constructProps = {
        feature,
        getIconGeometry,
        layerId,
        iconPath,
        styleKey,
        populateIcon
      }
      if (Icons[iconPath]) {
        constructStyleWithStrokedSelect({...constructProps, svgString: Icons[iconPath]})
      } else {
        Promise.all([
          getIcon(`${iconPath}`, feature),
          loadSelectedIcon ? getIcon(`${iconPath}_selected`, feature) : ""
        ]).then(
          ([svgString, selectedSvgString]) => {
            constructStyleWithStrokedSelect({...constructProps, svgString, selectedSvgString})
          },
          (e) => alertErrorIcon(iconPath, feature)
        )
      }
    }
  }
  const style = styleFn ? styleFn(feature, resolution) : new Style()
  if (feature.getGeometry()?.getType() === "Point") {
    const point = feature.getGeometry() as Point
    const [x, y] = mapService.getPixelFromCoordinate(point.getCoordinates())
    style.setZIndex(y)
  }
  return [...getExtraStyles(feature, resolution), style]
}

///////////////////////////////////// poi ///////////////////////////////////////////////////

const PoiSelBackIcons: {[key: string]: string} = {}

const getSelectedSvg = (color: number) =>
  `<svg width="60" height="83" viewBox="0 0 60 83" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M29.9252 67.9302C34.7132 56.2594 59.8504 46.4524 59.8504 29.9252C59.8504 13.398 46.4524 0 29.9252 0C13.398 0 0 13.398 0 29.9252C0 46.4524 25.1372 56.2594 29.9252 67.9302Z" fill="${colors[color][0]}" />
    <path d="M30 81.9302L26.1029 71.4302H33.8971L30 81.9302Z" fill="${colors[color][0]}" />
  </svg>
  `
const getCircleStyle = (color: number) => new Circle({
  radius: 30,
  fill: new Fill({color: colors[color][0]}),
  stroke: new Stroke({color: "#FFF", width: 1})
})

const PoiStyles: {[key: string]: (feature: FeatureLike, resolution: number) => Style[]} = {}

export const stylePoiElement = (
  feature: FeatureLike,
  resolution: number
) => {
  const {color, type} = feature.getProperties() as TPoiProps

  const fraction: Partial<Options> = {anchorXUnits: "fraction", anchorYUnits: "fraction", crossOrigin: "*"}
  const icon: Partial<Options> = {src: `/poi/${type}.svg`, height: 60, width: 60}

  let style = PoiStyles[`${type}_${color}`]
  if (!style) {
    style = (f: FeatureLike, res: number) => {
      const {color, type} = f.getProperties() as TPoiProps
      let styles = []
      if (f.getProperties().selected) {
        if (!PoiSelBackIcons[`${type}_${color}`]) {
          PoiSelBackIcons[`${type}_${color}`] = URL.createObjectURL(new Blob([getSelectedSvg(color)], {type: 'image/svg+xml'}))
        }
        const urlSelected = PoiSelBackIcons[`${type}_${color}`]
        styles = [
          new Style({image: new Icon({src: urlSelected, anchor: [0.5, 1], ...fraction})}),
          new Style({image: new Icon({anchor: [0.5, 1.42], ...icon, ...fraction})})
        ]
      } else {
        styles = [
          new Style({image: getCircleStyle(color)}),
          new Style({image: new Icon({anchor: [0.5, 0.5], ...icon, ...fraction})})
        ]
      }
      const zIndex = Number(f.getId()) * (f.getProperties().selected ? 100000 : 10000)
      return styles.map((s, i) => {
        s.setGeometry(f.getGeometry() as Point)
        s.setZIndex(zIndex)
        return s
      })
    }
    PoiStyles[`${type}_${color}`] = style
  }
  return style(feature, resolution)
}

export const stylePoiCluster = (
  features: Feature<Point>[],
  resolution: number
) => {
  if (features.length < 2) return features.flatMap(feature => stylePoiElement(feature, resolution))

  let style = PoiStyles[`poi_cluster_${features.length}`] as () => Style[]
  if (!style) {
    style = () => {
      return [
        new Style({image: getCircleStyle(0), zIndex: 1}),
        new Style({
          text: new Text({
            text: features.length.toString(),
            fill: new Fill({color: "#FFF"}),
            font: "700 24px 'Open Sans', sans-serif",
          }),
          zIndex: 1,
        })
      ]
    }
    PoiStyles[`poi_cluster_${features.length}`] = style
  }
  return style()
}

export const stylePoi = (
  feature: FeatureLike,
  resolution: number
): Style[] => {
  const features = ((feature.get("features") || []) as Feature<Point>[])
    .filter(feature => {
      const {type, selected} = feature.getProperties() || {}
      return store.layer.list?.includes(type) || selected
    })
  if (resolution < 1.7) return features.flatMap(feature => stylePoiElement(feature, resolution))

  return [
    ...features
      .filter(feature => feature.getProperties().selected)
      .flatMap(feature => stylePoiElement(feature, resolution)),
    ...stylePoiCluster(features.filter(feature => !feature.getProperties().selected), resolution)
  ]
}
